// @flow
import * as React from 'react';

import { connect } from 'react-redux';
import { compose } from 'redux';

import { NOTIFICATION_ID as CART_NOTIFICATION_ID } from '@gandi/cart';
import {
  notifyDanger,
  notifyWarning,
  closeNotification,
} from '@gandi/notifications';
import type { NotifyAction } from '@gandi/notifications';
import { type TranslatorProps, withTranslator } from '@gandi/translate';
import {
  Base,
  Badge,
  Cell,
  PopoverContainer,
  Popover,
  Fqdn,
  Text,
} from '@gandi/ui';
import { ListRow, ListItem } from '@gandi/ui/deprecated';

import CellRowGroup from '../../DomainTransfer/components/DomainList/CellRowGroup';
import CellRowToCol from '../../DomainTransfer/components/DomainList/CellRowToCol';

import { addToCart, removeFromCart } from '../modules/actions';
import type { DomainStatus, ExtendedResult } from '../types';

import CartButton from './CartButton';
import FallbackRow from './FallbackRow';

import styles from './DomainRow.scss';

type DispatchProps = {
  addToCart: (ExtendedResult, TranslatorProps) => Promise<any>,
  removeFromCart: ExtendedResult => Promise<any>,
  notifyWarning: NotifyAction,
  notifyDanger: NotifyAction,
  closeNotification: string => void,
};

type OwnProps = {
  domain: ExtendedResult,
  translator: TranslatorProps,
  taxesIncluded: boolean,
};

type StateProps = {
  whoisURL: string,
  getFallbackMessage: string => string,
};

type Props = { ...DispatchProps, ...StateProps, ...OwnProps };

const popoverIconModifiers = {
  flip: {
    // This means, prefer the bottom placement,
    // but allow repositioning on all sides, not just top.
    behavior: ['bottom', 'top', 'left', 'right'],
  },
};

function renderOptionRows(options: Array<string>, __: TranslatorProps['__']) {
  const countMailboxes = options.filter(
    option => option === 'mailboxv2',
  ).length;
  const hasTrusteeOption = !!options.find(option => option === 'trustee');

  return (
    <>
      {countMailboxes > 0 && (
        <span>{__('%(count)d mailboxes', { count: countMailboxes })}</span>
      )}
      {hasTrusteeOption && <span>{__('Trustee')}</span>}
    </>
  );
}

function renderGlobalOptionsCount(
  options: Array<string>,
  __: TranslatorProps['__'],
) {
  return __('including %(count)d options', {
    count: options.length,
  });
}

function getDomainStatus(
  notAtGandi: boolean,
  domain: ExtendedResult,
): DomainStatus {
  if (notAtGandi && domain.transferable) return 'transferable';
  if (domain.invalid) return 'invalid';

  if (
    domain.extraneous &&
    domain.availability === 'available' &&
    !domain.renewable &&
    !domain.restorable &&
    !domain.transferable &&
    !domain.loading
  )
    return 'registerable';

  if (
    !domain.extraneous &&
    !domain.renewable &&
    !domain.restorable &&
    !domain.loading
  )
    return 'unavailable';

  if (notAtGandi && !domain.transferable) return 'untransferable';

  return 'error';
}

class DomainRow extends React.PureComponent<Props> {
  handleError: (error: Error & any) => void = error => {
    const {
      translator: { __ },
    } = this.props;

    this.props.closeNotification(CART_NOTIFICATION_ID);

    if (error.response && error.response.data && error.response.data.message) {
      if (error.response.status === 403) {
        this.props.notifyWarning({
          message: error.response.data.message,
          title: __('Insufficient permissions'),
        });
      } else {
        this.props.notifyDanger({
          message: error.response.data.message,
        });
      }
    } else {
      this.props.notifyDanger(__('Unexpected error occurred'));
    }
  };

  onClickItem: (evt: SyntheticMouseEvent<>) => Promise<void> = async (
    evt: SyntheticMouseEvent<>,
  ) => {
    evt.preventDefault();

    const { domain, translator } = this.props;

    try {
      if (domain.addedToCart) {
        await this.props.removeFromCart(domain);
      } else {
        await this.props.addToCart(domain, translator);
      }
    } catch (error) {
      this.handleError(error);
    }
  };

  renderPrice(): null | React.MixedElement {
    const {
      domain: { quotation },
      taxesIncluded,
    } = this.props;
    if (!quotation || !quotation.price || !quotation.duration) {
      return null;
    }

    const amount = taxesIncluded
      ? quotation.price.afterTaxes
      : quotation.price.beforeTaxes;
    const currency = quotation.currency;
    const minDuration = quotation.duration;

    const { __, formatPrice } = this.props.translator;

    const durationTranslation =
      minDuration > 1
        ? __('%(count)s years', {
            count: minDuration,
          })
        : __('/year');

    return (
      <span>
        <span>{formatPrice(amount, currency)}</span>{' '}
        <Text color="light">{durationTranslation}</Text>
      </span>
    );
  }

  renderOptions(): null | React.Node {
    const {
      translator: { __ },
      domain: { quotation, fqdn },
    } = this.props;

    if (
      !quotation ||
      !Array.isArray(quotation.options) ||
      !quotation.options.length
    ) {
      return null;
    }

    return (
      <PopoverContainer
        id={`quoted-product-${fqdn}-option`}
        behaviour="click"
        renderReference={getReferenceProps => (
          <Base
            size="small"
            color="green"
            placement="bottom"
            modifiers={popoverIconModifiers}
            tabIndex={0}
            role="button"
            className={styles.quotedProductOptions}
            {...getReferenceProps()}
          >
            {/* $FlowFixMe: above we return null if no options */}
            {renderGlobalOptionsCount(quotation.options, __)}
          </Base>
        )}
      >
        {getPopoverProps => (
          <Popover {...getPopoverProps()}>
            {/* $FlowFixMe: above we return null if no options */}
            {renderOptionRows(quotation.options, __)}
          </Popover>
        )}
      </PopoverContainer>
    );
  }

  render(): React.Node {
    const { translator, domain } = this.props;

    const renewableOrRestorable = domain.restorable || domain.renewable;

    const notAtGandi =
      domain.extraneous &&
      !domain.renewable &&
      !domain.restorable &&
      domain.availability === 'unavailable';

    const displayFallback =
      (!renewableOrRestorable || domain.extraneous) && !domain.loading;

    return (
      <ListRow>
        <ListItem className={styles.item} backgroundColor="white">
          {displayFallback && (
            <FallbackRow
              fqdn={domain.fqdn}
              notAtGandi={!!notAtGandi}
              domainStatus={getDomainStatus(!!notAtGandi, domain)}
            />
          )}

          {!displayFallback && (
            <>
              <CellRowGroup>
                <Cell flexGrow1>
                  <Fqdn maxLength={30} className={styles.fqdn}>
                    {domain.fqdn}
                  </Fqdn>
                </Cell>
                {!domain.extraneous &&
                  renewableOrRestorable &&
                  !domain.loading && (
                    <Cell>
                      <CellRowToCol className={styles.textRightDesktop}>
                        {domain.restorable && (
                          <Badge intent="danger" className={styles.badge}>
                            {translator.__('Restorable')}
                          </Badge>
                        )}
                        {this.renderPrice()}
                        {this.renderOptions()}
                      </CellRowToCol>
                    </Cell>
                  )}
              </CellRowGroup>
              {(renewableOrRestorable || domain.loading) && (
                <CartButton
                  loading={
                    domain.loading ||
                    ['adding', 'removing'].includes(domain.cartProductState)
                  }
                  addedToCart={domain.addedToCart}
                  renewable={domain.renewable}
                  restorable={domain.restorable}
                  onClick={this.onClickItem}
                />
              )}
            </>
          )}
        </ListItem>
      </ListRow>
    );
  }
}

const mapDispatchToProps = {
  addToCart,
  removeFromCart,
  notifyWarning,
  notifyDanger,
  closeNotification,
};

export default (compose(
  withTranslator({ propsNamespace: 'translator' }),
  connect(null, mapDispatchToProps),
)(DomainRow): React.ComponentType<Omit<OwnProps, 'translator'>>);
